import { inject, Injectable } from '@angular/core';
import { IndexedDbFile } from '@models/indexed-db-file';
import { IndexedDbItem } from '@models/indexed-db-item';
import { Metadata } from '@models/metadata';
import { FileService } from '@services/file.service';
import { catchError, finalize, from, map, Observable, switchMap, throwError } from 'rxjs';

@Injectable({
  providedIn: 'root',
})
export class IndexedDbService {
  private fileService = inject(FileService);

  /**
   * Saves an item to the store.
   *
   * If the item data is a File, it will be converted to ArrayBuffer before saving.
   *
   * @param {string} dbName name of the database
   * @param {T} rawData data to save
   * @param {string} key key for the item
   * @param {Metadata} metadata optional metadata for the item
   *
   * @returns {Observable<IDBValidKey>} observable with the key of the saved item
   */
  public saveItem<T>(dbName: string, rawData: T, key: string, metadata?: Metadata): Observable<IDBValidKey> {
    return this.processBeforeSave(rawData).pipe(
      switchMap((data) =>
        this.executeTransaction<IDBValidKey>(dbName, 'readwrite', (store) =>
          from(this.promisifyRequest<IDBValidKey>(store.put({ key, data, metadata }))),
        ),
      ),
    );
  }

  /**
   * Retrieves all items from the store.
   *
   * If an item was stored as ArrayBuffer (originally a File), it will be converted back to a File.
   *
   * @param {string} dbName name of the database
   *
   * @returns {Observable<IndexedDbItem<T>[]>} observable with an array of all stored items
   */
  public getAllItems<T>(dbName: string): Observable<IndexedDbItem<T>[]> {
    return this.executeTransaction<IndexedDbItem<T>[]>(dbName, 'readonly', (store) =>
      from(this.promisifyRequest<IndexedDbItem<T>[]>(store.getAll())),
    ).pipe(
      map((items) =>
        items.map((item) => ({
          ...item,
          data: this.processAfterRetrieve(item.data),
        })),
      ),
    );
  }

  /**
   * Deletes an item from the store.
   *
   * @param {string} dbName name of the database
   * @param {IDBValidKey} key key of the item to delete
   *
   * @returns {Observable<void>} observable indicating successful deletion
   */
  public deleteItem(dbName: string, key: IDBValidKey): Observable<void> {
    return this.executeTransaction<void>(dbName, 'readwrite', (store) =>
      from(this.promisifyRequest<undefined>(store.delete(key))).pipe(map(() => undefined)),
    );
  }

  /**
   * Deletes an entire database.
   *
   * @param {string} dbName name of the database to delete
   *
   * @returns {Observable<void>} observable indicating successful deletion
   */
  public deleteDatabase(dbName: string): Observable<void> {
    return new Observable<void>((observer) => {
      const request = indexedDB.deleteDatabase(dbName);

      request.onsuccess = () => {
        observer.next();
        observer.complete();
      };

      request.onerror = (event) => observer.error(`Error deleting database: ${(event.target as IDBRequest).error}`);
    });
  }

  /**
   * Gets the names of all existing IndexedDB databases.
   *
   * @returns {Observable<string[]>} observable with array of database names
   */
  public getAllDatabaseNames(): Observable<string[]> {
    return from(indexedDB.databases()).pipe(map((databases) => databases.map((db) => db.name).filter(Boolean)));
  }

  /**
   * Processes data before saving.
   *
   * If the raw data is a File, it's converted to ArrayBuffer with metadata.
   *
   * @param {any} rawData data to process
   *
   * @returns {Observable<any>} observable with the processed data
   */
  private processBeforeSave(rawData: any): Observable<any> {
    if (rawData instanceof File) {
      return this.fileService.convertFileToArrayBuffer(rawData).pipe(
        map(
          (arrayBuffer): IndexedDbFile => ({
            type: rawData.type,
            name: rawData.name,
            size: rawData.size,
            lastModified: rawData.lastModified,
            arrayBufferData: arrayBuffer,
          }),
        ),
      );
    }

    return new Observable((observer) => {
      observer.next(rawData);
      observer.complete();
    });
  }

  /**
   * Processes an item after retrieval.
   *
   * If the item was originally a File (stored as ArrayBuffer), it's converted back to a File.
   *
   * @param {any} item item to process
   *
   * @returns {any} the processed item
   */
  private processAfterRetrieve(item: any): any {
    if (item && typeof item === 'object' && 'arrayBufferData' in item) {
      return this.fileService.convertArrayBufferToFile(item.arrayBufferData, item.name, item.type, item.lastModified);
    }

    return item;
  }

  /**
   * Opens a connection to the specified database.
   *
   * It creates the database with appropriate object store if it doesn't exist.
   *
   * @param {string} dbName name of the database
   *
   * @returns {Observable<IDBDatabase>} observable with the database connection
   */
  private openConnection(dbName: string): Observable<IDBDatabase> {
    return new Observable<IDBDatabase>((observer) => {
      const request = indexedDB.open(dbName, 1);

      request.onupgradeneeded = (event: IDBVersionChangeEvent) => {
        const db = (event.target as IDBOpenDBRequest).result;
        const storeName = this.getStoreName(dbName);

        if (!db.objectStoreNames.contains(storeName)) {
          db.createObjectStore(storeName, { keyPath: 'key' });
        }
      };

      request.onsuccess = (event: Event) => {
        observer.next((event.target as IDBOpenDBRequest).result);
        observer.complete();
      };

      request.onerror = (event: Event) =>
        observer.error(`Error opening database: ${(event.target as IDBOpenDBRequest).error}`);
    });
  }

  /**
   * Executes a transaction on the specified object store.
   *
   * It opens a new connection for each transaction and closes it when done.
   *
   * @param {string} dbName name of the database
   * @param {IDBTransactionMode} mode transaction mode (readonly or readwrite)
   * @param {(store: IDBObjectStore) => Observable<T>} callback function to execute the transaction
   *
   * @returns {Observable<T>} observable with the transaction result
   */
  private executeTransaction<T>(
    dbName: string,
    mode: IDBTransactionMode,
    callback: (store: IDBObjectStore) => Observable<T>,
  ): Observable<T> {
    return this.openConnection(dbName).pipe(
      switchMap((connection) => {
        try {
          const storeName = this.getStoreName(dbName);
          const transaction = connection.transaction(storeName, mode);
          const store = transaction.objectStore(storeName);

          return callback(store).pipe(
            finalize(() => connection.close()),
            catchError((err) => throwError(() => new Error(`Transaction error: ${err}`))),
          );
        } catch (error) {
          connection.close();

          return throwError(() => new Error(`Error creating transaction: ${error}`));
        }
      }),
    );
  }

  /**
   * Wraps an IndexedDB request in a Promise.
   *
   * @param {IDBRequest<T>} request indexeddb request to wrap
   *
   * @returns {Promise<T>} promise resolving with the request result
   */
  private promisifyRequest<T>(request: IDBRequest<T>): Promise<T> {
    return new Promise<T>((resolve, reject) => {
      request.onsuccess = () => resolve(request.result);
      request.onerror = () => reject(request.error);
    });
  }

  /**
   * Gets the standardized store name for a given database.
   *
   * @param {string} dbName name of the database
   *
   * @returns {string} the standardized store name
   */
  private getStoreName(dbName: string): string {
    return `${dbName}-store`;
  }
}
